import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { WrenchIcon, SignalIcon, LightningIcon, TrophyIcon, GlobeIcon, BriefcaseIcon } from '../components/Icons';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(245, 184, 0, 0.3); }
  50% { box-shadow: 0 0 40px rgba(245, 184, 0, 0.6); }
`;

const HomeContainer = styled.div`
  min-height: 100vh;
  position: relative;
`;

const VideoHero = styled.section`
  position: relative;
  height: 85vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    height: 70vh;
  }
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  z-index: 0;
  object-fit: cover;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(14, 17, 20, 0.85) 0%, rgba(44, 47, 51, 0.75) 100%);
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  color: white;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 7vw, 6rem);
  margin-bottom: 1.5rem;
  animation: ${fadeInUp} 1s ease;
  font-weight: 900;
  line-height: 1.1;
  color: #F5B800;
  text-shadow: 0 0 40px rgba(245, 184, 0, 0.6), 0 4px 20px rgba(0, 0, 0, 0.8);
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 3vw, 1.8rem);
  margin-bottom: 2.5rem;
  animation: ${fadeInUp} 1s ease 0.2s both;
  line-height: 1.6;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: #F5B800;
  color: #0E1114;
  padding: 1.5rem 4rem;
  border-radius: 4px;
  font-weight: 800;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 1s ease 0.4s both, ${glow} 3s ease-in-out infinite;
  box-shadow: 0 10px 30px rgba(245, 184, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Space Grotesk', sans-serif;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(245, 184, 0, 0.6);
    background: #2EC4B6;
  }

  @media (max-width: 768px) {
    padding: 1.25rem 2.5rem;
    font-size: 1.1rem;
  }
`;

const FeaturesSection = styled.section`
  padding: 5rem 2rem;
  background: var(--bg-secondary);
  position: relative;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 1px;
  transition: color 0.3s ease;

  &:after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    background: #F5B800;
    margin: 1.5rem auto 0;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2vw, 1.3rem);
  color: var(--text-secondary);
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const FeaturesGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled.div`
  background: var(--card-bg);
  padding: 2.5rem 2rem;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  border-left: 4px solid transparent;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(245, 184, 0, 0.2);
    border-left-color: #F5B800;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;

    &:hover {
      transform: translateY(-5px);
    }
  }
`;


const FeatureTitle = styled.h3`
  font-size: clamp(1.3rem, 2vw, 1.6rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
  transition: color 0.3s ease;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: clamp(0.95rem, 1.5vw, 1.05rem);
  transition: color 0.3s ease;
`;

const StatsSection = styled.section`
  background: #2C2F33;
  padding: 4rem 2rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #F5B800, #2EC4B6, #0077B6);
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: clamp(3rem, 6vw, 4.5rem);
  font-weight: 900;
  color: #F5B800;
  margin-bottom: 0.5rem;
  font-family: 'Space Grotesk', sans-serif;
  text-shadow: 0 0 30px rgba(245, 184, 0, 0.5);
`;

const StatLabel = styled.div`
  font-size: clamp(0.9rem, 1.5vw, 1.1rem);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InfoSection = styled.section`
  background: linear-gradient(135deg, #0E1114 0%, #2C2F33 100%);
  padding: 5rem 2rem;
  text-align: center;
  position: relative;
  color: white;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const InfoContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const InfoTitle = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: white;
  margin-bottom: 2rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  span {
    color: #F5B800;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const InfoText = styled.p`
  font-size: clamp(1rem, 2vw, 1.2rem);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
  line-height: 1.9;
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 4px;
  border: 2px solid #F5B800;

  > div {
    margin-bottom: 1rem;
    color: white;
    font-size: clamp(1rem, 2vw, 1.1rem);

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #F5B800;
    font-weight: 700;
    transition: all 0.3s ease;
    padding: 0.5rem 1rem;
    border-radius: 4px;

    &:hover {
      background: rgba(245, 184, 0, 0.1);
      color: #2EC4B6;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    margin-top: 2rem;
  }
`;

const Home = () => {
  return (
    <HomeContainer>
      <VideoHero>
        <VideoBackground autoPlay loop muted playsInline>
          <source src="/videos/14546784_1080_1920_30fps.mp4" type="video/mp4" />
        </VideoBackground>
        <VideoOverlay />
        <HeroContent>
          <HeroTitle>Enterprise-Scale Telecommunications & Engineering Solutions</HeroTitle>
          <HeroSubtitle>
            Over 20 Years of Excellence Serving Large-Scale Telecommunications Providers & Industrial Infrastructure
          </HeroSubtitle>
          <CTAButton to="/contact">Get Started Today</CTAButton>
        </HeroContent>
      </VideoHero>

      <FeaturesSection>
        <SectionTitle>Why Choose Vital-Link?</SectionTitle>
        <SectionSubtitle>Industry-leading expertise from server rack to endpoint</SectionSubtitle>
        <FeaturesGrid>
          <FeatureCard>
            <WrenchIcon />
            <FeatureTitle>Industrial-Scale Installation</FeatureTitle>
            <FeatureDescription>
              Enterprise-grade structured wiring and cable management solutions for large-scale telecommunications providers with meticulous attention to detail and industry-leading practices.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <SignalIcon />
            <FeatureTitle>Advanced Engineering</FeatureTitle>
            <FeatureDescription>
              Specialized in DOCSIS 3.0/3.1, XGS-PON, network infrastructure, and cutting-edge telecommunications systems.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <LightningIcon />
            <FeatureTitle>Quality Assurance</FeatureTitle>
            <FeatureDescription>
              Certified DOCSIS Engineer Professional (DEP) with PON QA testing and rigorous validation protocols for large-scale deployments and guaranteed performance.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <TrophyIcon />
            <FeatureTitle>20+ Years Experience</FeatureTitle>
            <FeatureDescription>
              Proven track record working with industry leaders like Cisco Systems, Casa Systems, and Arris International.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <GlobeIcon />
            <FeatureTitle>Fiber & Coax Auditing</FeatureTitle>
            <FeatureDescription>
              Comprehensive auditing services for fiber and coaxial networks, ensuring optimal performance and compliance for large-scale providers.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <BriefcaseIcon />
            <FeatureTitle>Headend & Central Office</FeatureTitle>
            <FeatureDescription>
              Specialized installations and engineering for headend facilities and central office infrastructure serving large-scale telecommunications providers.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <StatsSection>
        <StatsGrid>
          <StatItem>
            <StatNumber>20+</StatNumber>
            <StatLabel>Years Experience</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>100%</StatNumber>
            <StatLabel>Client Satisfaction</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>DEP</StatNumber>
            <StatLabel>Certified Professional</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>Support Available</StatLabel>
          </StatItem>
        </StatsGrid>
      </StatsSection>

      <InfoSection>
        <InfoContent>
          <InfoTitle>Serving <span>the United States</span> Nationwide</InfoTitle>
          <InfoText>
            Vital-Link Structured Wiring and Advanced Engineering LLC specializes exclusively in large-scale industrial and enterprise telecommunications infrastructure. We serve telecommunications providers with headend and central office installations, network engineering, and comprehensive infrastructure solutions. With over two decades of experience in the industry, we bring expertise, reliability, and precision to every large-scale project.
          </InfoText>
          <InfoText>
            We do not provide residential or small-scale installations. Our focus is exclusively on industrial-scale deployments for telecommunications providers, including fiber and coax auditing, PON QA testing, headend installations, and central office engineering services.
          </InfoText>
          <ContactInfo>
            <div>Ready to discuss your project?</div>
            <div>
              Call us at <a href="tel:850-730-0401">850-730-0401</a> or{' '}
              <Link to="/contact">contact us online</Link>
            </div>
          </ContactInfo>
        </InfoContent>
      </InfoSection>
    </HomeContainer>
  );
};

export default Home;
